import {useBoardCardHeadingLevel} from '@github-ui/board-core'
import {GitHubAvatar} from '@github-ui/github-avatar'
import {IssueStateIcons, PullRequestStateIcons} from '@github-ui/issue-pr-state/Icons'
import {RepositoryLabelToken} from '@github-ui/label-token/RepositoryLabelToken'
import {Link as RouterLink} from '@github-ui/react-router'
import {isColorName, useNamedColor} from '@github-ui/use-named-color'
import {MilestoneIcon} from '@primer/octicons-react'
import {
  ActionList,
  AnchoredOverlay,
  AvatarStack,
  Button,
  Link as PrimerLink,
  RelativeTime,
  Token,
  Tooltip,
} from '@primer/react'
import {clsx} from 'clsx'
import {type MouseEvent, useCallback, useId, useRef, useState} from 'react'

import type {FieldDefinition, ItemType} from '../../saved-view-options/fields'
import type {
  BoardPayloadAssignee,
  BoardPayloadIssueType,
  BoardPayloadItem,
  BoardPayloadLabel,
  BoardPayloadMilestone,
  BoardPayloadRepository,
} from './board-payload'
import {RepositoryIcon} from './RepositoryIcon'
import {type CardIssueFields, resolveVisibleCardFields} from './visible-card-fields'
import styles from './BoardCard.module.css'

/** What a host needs to open a card's item somewhere other than the item's own page. */
export interface BoardItemIdentifier {
  owner: string
  repo: string
  number: number
  globalId: string
  permalink: string
  isAccountIssue?: boolean
}

/**
 * Icons come from `@github-ui/issue-pr-state`, so every surface that shows issue/PR state
 * agrees on the glyph, colour, and accessible name.
 */
function StateIcon({item}: {item: BoardPayloadItem}) {
  if (item.type === 'issue') {
    if (item.state === 'open') return <IssueStateIcons.OPEN size={12} />
    if (item.stateReason === 'not_planned') return <IssueStateIcons.NOT_PLANNED size={12} />
    if (item.stateReason === 'duplicate') return <IssueStateIcons.DUPLICATE size={12} />
    return <IssueStateIcons.COMPLETED size={12} />
  }

  switch (item.state) {
    case 'draft':
      return <PullRequestStateIcons.DRAFT size={12} />
    case 'merged':
      return <PullRequestStateIcons.MERGED size={12} />
    case 'closed':
      return <PullRequestStateIcons.CLOSED size={12} />
    case 'queued':
      return <PullRequestStateIcons.QUEUED size={12} />
    case 'archived':
      return <PullRequestStateIcons.ARCHIVED size={12} />
    default:
      return <PullRequestStateIcons.OPEN size={12} />
  }
}

/**
 * One card, drawn from a payload item.
 *
 * `visibleFieldIdentifiers` names the attributable fields the card should render, keyed by
 * the same field identifier the payload uses (`label`, `assignee`, `milestone`, `repository`,
 * `issue_type`, `field.<id>`). `fieldDefinitions` says which custom fields are single- or
 * multi-selects; without it, no custom field is drawn.
 */
export function BoardCard({
  item,
  visibleFieldIdentifiers,
  fieldDefinitions,
  onItemSelect,
}: {
  item: BoardPayloadItem
  visibleFieldIdentifiers: ReadonlySet<string>
  fieldDefinitions?: readonly FieldDefinition[]
  /**
   * Handler that is called when the title is clicked, which returns whether it opened the item.
   * Returning `false`, or a modified click, follows the title link instead.
   */
  onItemSelect?: (itemType: ItemType, item: BoardItemIdentifier, returnFocusElement: HTMLElement) => boolean
}) {
  const {labels, assignees, milestone, repository, issueType, issueFields, created, updated, closed} =
    resolveVisibleCardFields(item, visibleFieldIdentifiers, fieldDefinitions)
  const itemUrl = `/${item.repoNameWithOwner}/${item.type === 'issue' ? 'issues' : 'pull'}/${item.number}`
  const TitleHeading = `h${useBoardCardHeadingLevel()}` as const

  const handleTitleClick = onItemSelect
    ? (event: MouseEvent<HTMLAnchorElement>) => {
        if (event.defaultPrevented || !isPlainLeftClick(event)) return
        // The clicked anchor rather than `document.activeElement`, which a click does not move in Safari.
        if (onItemSelect(item.type, toItemIdentifier(item), event.currentTarget)) event.preventDefault()
      }
    : undefined

  return (
    <div className={styles.card}>
      <div className={styles.reference}>
        <span className={styles.referenceText}>
          <StateIcon item={item} />
          {`${item.repoNameWithOwner} #${item.number}`}
        </span>
        <Assignees assignees={assignees} />
      </div>

      <TitleHeading className={styles.titleHeading}>
        <PrimerLink as={RouterLink} to={itemUrl} className={styles.title} onClick={handleTitleClick}>
          {item.title}
        </PrimerLink>
      </TitleHeading>

      <Fields
        issueType={issueType}
        issueFields={issueFields}
        created={created}
        updated={updated}
        closed={closed}
        repository={repository}
        milestone={milestone}
        labels={labels}
        repoNameWithOwner={item.repoNameWithOwner}
      />
    </div>
  )
}

/**
 * Links to the repository. The token cuts a long name off with an ellipsis at card width, so
 * the tooltip carries the full name. It is a description rather than the link's label: the
 * link's own name also says whether the repository is public, private, forked or archived.
 */
function RepositoryToken({repository}: {repository: BoardPayloadRepository}) {
  return (
    <Tooltip text={repository.nameWithOwner} type="description" direction="n" className={styles.fieldTooltip}>
      <Token
        as="a"
        href={`/${repository.nameWithOwner}`}
        leadingVisual={() => <RepositoryIcon size={14} status={repository.repositoryStatus} />}
        text={repository.nameWithOwner}
      />
    </Tooltip>
  )
}

/** Meta, Ctrl, Shift and Alt clicks open a new tab, a new window or a download. */
function isPlainLeftClick(event: MouseEvent<HTMLAnchorElement>): boolean {
  // eslint-disable-next-line @github-ui/ui-commands/no-manual-shortcut-logic -- a modified link click, not a keyboard shortcut
  return event.button === 0 && !event.metaKey && !event.ctrlKey && !event.shiftKey && !event.altKey
}

function toItemIdentifier(item: BoardPayloadItem): BoardItemIdentifier {
  const [owner = '', repo = ''] = item.repoNameWithOwner.split('/')
  return {
    owner,
    repo,
    number: item.number,
    globalId: item.globalId,
    permalink: item.permalink,
    ...(item.type === 'issue' && item.isAccountIssue ? {isAccountIssue: true} : {}),
  }
}

/**
 * Each avatar links to its assignee with a tooltip that shows the login.
 */
function Assignees({assignees}: {assignees: readonly BoardPayloadAssignee[]}) {
  if (assignees.length === 0) return null

  return (
    <span className={styles.assignees}>
      {/* 24px avatars so each target meets WCAG 2.5.8 minimum. The "equivalent control"
          exception doesn't apply on the board view — no other 24×24 control on the page
          links to the assignee's profile. */}
      <AvatarStack alignRight size={24}>
        {assignees.map(assignee => (
          <AssigneeLink key={assignee.login} assignee={assignee} />
        ))}
      </AvatarStack>
    </span>
  )
}

function AssigneeLink({assignee, className}: {assignee: BoardPayloadAssignee; className?: string}) {
  return (
    <span className={clsx(className, styles.assigneeItem)}>
      <Tooltip text={assignee.login} type="label" direction="n">
        <PrimerLink href={`/${assignee.login}`}>
          <GitHubAvatar alt={assignee.login} src={assignee.avatarUrl} size={24} />
        </PrimerLink>
      </Tooltip>
    </span>
  )
}

/**
 * How many labels a card names before the rest roll into the overflow.
 *
 * Three, where the issues index caps nothing and lets every label it fetched run inline. A
 * card is a fifth of a row's width, so a row's answer would be several lines of tokens on
 * one; the count is what a card has instead of the room.
 */
const MAX_INLINE_LABELS = 3

/**
 * How many of the collapsed names the count carries in its tooltip before it stops naming
 * them and counts the remainder. An issue can hold dozens of labels, and a description that
 * grows with every one of them is read out in full on focus.
 */
const MAX_NAMED_OVERFLOW_LABELS = 3

/**
 * The card's fields in one row: issue type, custom single-selects, then multi-selects, the
 * first few labels, a count standing for the rest of the labels, then repository, milestone,
 * and the created, updated and closed dates.
 *
 * The count opens a list of the labels it stands for, each a link to that label's issues.
 * A few of their names are on the count as well, so hovering reads them without opening
 * anything.
 */
function Fields({
  issueType,
  issueFields,
  created,
  updated,
  closed,
  repository,
  milestone,
  labels,
  repoNameWithOwner,
}: {
  issueType: BoardPayloadIssueType | null
  issueFields: CardIssueFields
  created: string | null
  updated: string | null
  closed: string | null
  repository: BoardPayloadRepository | null
  milestone: BoardPayloadMilestone | null
  labels: readonly BoardPayloadLabel[]
  repoNameWithOwner: string
}) {
  if (
    issueFields.singleSelect.length === 0 &&
    issueFields.multiSelect.length === 0 &&
    labels.length === 0 &&
    issueType === null &&
    repository === null &&
    milestone === null &&
    created === null &&
    updated === null &&
    closed === null
  ) {
    return null
  }

  const inline = labels.slice(0, MAX_INLINE_LABELS)
  const overflow = labels.slice(MAX_INLINE_LABELS)

  return (
    <div className={styles.fields}>
      {issueType === null ? null : <NamedColorToken name={issueType.name} color={issueType.color} />}
      {issueFields.singleSelect.map(({identifier, option}) => (
        <NamedColorToken key={identifier} name={option.name} color={option.color} />
      ))}
      {issueFields.multiSelect.flatMap(({identifier, options}) =>
        options.map(option => (
          <NamedColorToken key={`${identifier}:${option.id}`} name={option.name} color={option.color} />
        )),
      )}
      {inline.map(label => (
        <Label key={label.id} label={label} repoNameWithOwner={repoNameWithOwner} />
      ))}
      {overflow.length === 0 ? null : <LabelOverflow labels={overflow} repoNameWithOwner={repoNameWithOwner} />}
      {repository === null ? null : <RepositoryToken repository={repository} />}
      {milestone === null ? null : (
        <Token
          as="a"
          href={milestone.url}
          leadingVisual={() => <MilestoneIcon size={14} aria-label="Milestone" />}
          text={milestone.title}
        />
      )}
      {created === null ? null : <DateToken name="Created" datetime={created} />}
      {updated === null ? null : <DateToken name="Updated" datetime={updated} />}
      {closed === null ? null : <DateToken name="Closed" datetime={closed} />}
    </div>
  )
}

/**
 * A display-only date, like Memex's "Created: Oct 12, 2026". The server renders the UTC date and
 * the browser swaps in the viewer's local one. No title, since the card has no tooltip for it.
 */
function DateToken({name, datetime}: {name: string; datetime: string}) {
  return (
    <Token
      text={
        <>
          {name}:{' '}
          <RelativeTime
            datetime={datetime}
            format="datetime"
            weekday={OMIT_DATE_PART}
            month="short"
            day="numeric"
            year="numeric"
            noTitle
          />
        </>
      }
    />
  )
}

// relative-time adds a weekday to `format="datetime"` unless given ""; Primer's types don't allow "".
const OMIT_DATE_PART = '' as unknown as undefined

/**
 * A display-only pill in a named colour, styled like Memex's single-select tokens, for an
 * issue type or a select option. The name renders as plain text. Colours arrive
 * upper-case for issue types and lower-case for options; anything unrecognised is gray.
 */
function NamedColorToken({name, color}: {name: string; color: string}) {
  const upperColor = color.toUpperCase()
  const namedColor = useNamedColor(isColorName(upperColor) ? upperColor : 'GRAY')
  return (
    <Token text={name} style={{backgroundColor: namedColor.bg, color: namedColor.fg, borderColor: namedColor.border}} />
  )
}

/**
 * The labels a card had no room to name, as a count that opens them.
 *
 * A disclosure over a plain list of links rather than an `ActionMenu`: every entry only
 * navigates to a label's issues, and `role="menu"`/`role="menuitem"` must not stand for
 * navigation (`.github/agents/resources/accessibility/global-prohibitions.md`). Opening on
 * a click rather than on the hover: a list holding links has to be somewhere a pointer can
 * travel into and a keyboard can reach, and Primer's tooltip does not open that way. Only
 * the labels behind the count, since the ones in front of it are already named and already
 * linked.
 */
function LabelOverflow({labels, repoNameWithOwner}: {labels: readonly BoardPayloadLabel[]; repoNameWithOwner: string}) {
  const [open, setOpen] = useState(false)
  const anchorRef = useRef<HTMLElement>(null)
  const onOpen = useCallback(() => setOpen(true), [])
  const onClose = useCallback(() => setOpen(false), [])

  return (
    <AnchoredOverlay
      open={open}
      onOpen={onOpen}
      onClose={onClose}
      align="start"
      anchorRef={anchorRef}
      renderAnchor={anchorProps => (
        // Primer's tooltip owns the trigger ref, so the overlay is anchored through it.
        <Tooltip ref={anchorRef} direction="n" text={overflowSummary(labels)}>
          <Button
            {...anchorProps}
            // Every `aria-haspopup` value names a widget this overlay is not; `aria-expanded` alone
            // is what a disclosure reports.
            aria-haspopup={undefined}
            variant="invisible"
            size="small"
            className={styles.labelOverflowButton}
          >
            {`+${labels.length}`}
            <span className="sr-only">{labels.length === 1 ? ' more label' : ' more labels'}</span>
          </Button>
        </Tooltip>
      )}
    >
      <ActionList aria-label="More labels" className={styles.labelOverflowList}>
        {labels.map(label => (
          <ActionList.LinkItem key={label.id} href={labelHref(label, repoNameWithOwner)}>
            <RepositoryLabelToken label={toTokenLabel(label)} />
          </ActionList.LinkItem>
        ))}
      </ActionList>
    </AnchoredOverlay>
  )
}

/**
 * The names the count carries in its tooltip: the first few, then how many are left.
 */
function overflowSummary(labels: readonly BoardPayloadLabel[]): string {
  const named = labels.slice(0, MAX_NAMED_OVERFLOW_LABELS).map(label => label.name)
  const remaining = labels.length - named.length
  return remaining === 0 ? named.join(', ') : `${named.join(', ')} and ${remaining} more`
}

/**
 * Linked label token with a description tooltip. The description also lives in a hidden
 * span so screen readers reach it whether or not the tooltip is shown; the tooltip itself
 * is suppressed for labels that carry no description.
 */
function Label({label, repoNameWithOwner}: {label: BoardPayloadLabel; repoNameWithOwner: string}) {
  const tooltipId = useId()
  const description = label.description ?? ''
  return (
    <Tooltip
      text={description}
      type="description"
      className={clsx(styles.fieldTooltip, !label.description && styles.tooltipHidden)}
    >
      <PrimerLink href={labelHref(label, repoNameWithOwner)} aria-describedby={tooltipId} className={styles.labelLink}>
        <RepositoryLabelToken label={toTokenLabel(label)} interactive />
        <span className="sr-only" id={tooltipId}>
          {description}
        </span>
      </PrimerLink>
    </Tooltip>
  )
}

function labelHref(label: BoardPayloadLabel, repoNameWithOwner: string): string {
  return `/${repoNameWithOwner}/labels/${encodeURIComponent(label.name)}`
}

/**
 * `nameHTML` is what carries emoji markup; the payload spells it `nameHtml` and the shared
 * token expects `nameHTML`, so it is renamed here until the payload aligns.
 */
function toTokenLabel(label: BoardPayloadLabel) {
  return {name: label.name, nameHTML: label.nameHtml, color: label.color}
}
